import { useState } from "react";
import { services, contactInfo } from "../data/mock";
import { ChevronRight, Check } from "lucide-react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";

export const ServicesSection = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [ref, isVisible] = useScrollAnimation(0.1);

  return (
    <section ref={ref} id="services" className="py-[100px] md:py-[120px] bg-[#f8f7f2]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div
          className={`mb-16 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <p
            className="text-[12px] tracking-[0.2em] uppercase text-[#c9a84c] mb-4"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Nos services
          </p>
          <h2
            className="text-[30px] md:text-[36px] font-normal text-[#1a1d2e] leading-[1.2] mb-4"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Un service adapté à chaque besoin
          </h2>
          <div className="gold-line" />
        </div>

        {/* Tabs */}
        <div className="flex gap-0 mb-12 border-b border-[#1a1d2e]/10">
          {services.map((service, idx) => (
            <button
              key={service.id}
              onClick={() => setActiveTab(idx)}
              className={`relative px-6 py-4 text-[14px] font-normal transition-colors duration-200 ${
                activeTab === idx
                  ? "text-[#1a1d2e]"
                  : "text-[#1a1d2e]/40 hover:text-[#1a1d2e]/70"
              }`}
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {service.title}
              {activeTab === idx && (
                <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#c9a84c]" />
              )}
            </button>
          ))}
        </div>

        {/* Active Service Content */}
        {services.map((service, idx) => (
          <div
            key={service.id}
            className={`transition-opacity duration-300 ${
              activeTab === idx ? "opacity-100 block" : "opacity-0 hidden"
            }`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {/* Image(s) */}
              <div className="order-2 lg:order-1">
                {service.images && service.images.length > 1 ? (
                  <div className="grid grid-cols-2 gap-3">
                    {service.images.map((img, imgIdx) => (
                      <div key={imgIdx} className="relative overflow-hidden group">
                        <img
                          src={img}
                          alt={`${service.title} - photo ${imgIdx + 1}`}
                          className="w-full h-[200px] md:h-[280px] object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="relative">
                    <div className="absolute -top-3 -left-3 w-full h-full border border-[#c9a84c]/30 pointer-events-none" />
                    <div className="relative overflow-hidden group">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-[300px] md:h-[420px] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                      />
                      <div
                        className="absolute inset-0 pointer-events-none"
                        style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(255,255,255,0.08) 0%, transparent 55%)" }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="order-1 lg:order-2">
                <div className="flex items-center gap-3 mb-4">
                  <service.icon size={20} strokeWidth={1.5} className="text-[#c9a84c]" />
                  <span
                    className="text-[12px] tracking-[0.15em] uppercase text-[#c9a84c]"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {service.subtitle}
                  </span>
                </div>

                <h3
                  className="text-[24px] md:text-[28px] font-normal text-[#1a1d2e] mb-6 leading-[1.3]"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {service.title}
                </h3>

                <p
                  className="text-[16px] text-[#555] leading-[1.7] mb-8"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {service.description}
                </p>

                {/* Features list */}
                <ul className="space-y-3 mb-10">
                  {service.features.map((feature, fIdx) => (
                    <li key={fIdx} className="flex items-center gap-3">
                      <Check size={16} strokeWidth={1.5} className="text-[#c9a84c] flex-shrink-0" />
                      <span
                        className="text-[14px] text-[#1a1d2e]/80"
                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <a
                  href={contactInfo.phoneLink}
                  className="inline-flex items-center justify-center px-6 py-4 border border-[#1a1d2e] text-[#1a1d2e] text-[14px] font-medium tracking-[0.02em] hover:bg-[#1a1d2e] hover:text-white transition-all duration-200"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  Réserver ce service
                  <ChevronRight size={16} className="ml-2" />
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
